import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function Register() {
    const [params] = useSearchParams();
    const [role, setRole] = useState(params.get("as") === "startup" ? "startup" : "user");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [accepted, setAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!accepted) {
            toast.error("Please accept the Terms & Privacy Policy to continue");
            return;
        }
        setLoading(true);
        const res = await register({ name, email, password, role });
        setLoading(false);
        if (res.ok) {
            toast.success(`Welcome, ${res.user.name}`);
            navigate(res.user.role === "startup" ? "/dashboard" : "/discover");
        } else {
            toast.error(res.error);
        }
    };

    return (
        <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ sign up</p>
                <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tighter uppercase mb-2">Join the pack.</h1>
                <p className="text-white/60 text-sm mb-8">Pick your side.</p>

                <div className="flex gap-2 mb-6 p-1 bg-brand-ink border border-white/10 rounded-md">
                    <button
                        type="button" data-testid="register-role-user"
                        onClick={() => setRole("user")}
                        className={`flex-1 py-3 font-mono-tag text-xs uppercase rounded transition-colors ${role === "user" ? "bg-brand-saffron text-black" : "text-white/60 hover:text-white"}`}
                    >Shopper</button>
                    <button
                        type="button" data-testid="register-role-startup"
                        onClick={() => setRole("startup")}
                        className={`flex-1 py-3 font-mono-tag text-xs uppercase rounded transition-colors ${role === "startup" ? "bg-brand-saffron text-black" : "text-white/60 hover:text-white"}`}
                    >Startup</button>
                </div>

                <form onSubmit={submit} className="space-y-5">
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">{role === "startup" ? "Founder name" : "Your name"}</Label>
                        <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                            className="mt-2 bg-brand-ink border-white/15 h-12 rounded-md" />
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Email</Label>
                        <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                            className="mt-2 bg-brand-ink border-white/15 h-12 rounded-md" />
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Password</Label>
                        <Input data-testid="register-password-input" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required
                            className="mt-2 bg-brand-ink border-white/15 h-12 rounded-md" />
                    </div>

                    <label className="flex items-start gap-3 pt-2 cursor-pointer group">
                        <Checkbox
                            data-testid="register-terms-checkbox"
                            checked={accepted}
                            onCheckedChange={(v) => setAccepted(!!v)}
                            className="mt-0.5 border-white/30 data-[state=checked]:bg-brand-saffron data-[state=checked]:border-brand-saffron data-[state=checked]:text-black"
                        />
                        <span className="text-xs text-white/70 leading-relaxed select-none">
                            I've read and agree to the{" "}
                            <Link to="/terms" target="_blank" data-testid="register-terms-link" className="text-brand-saffron hover:underline">Terms & Conditions</Link>
                            {" "}and{" "}
                            <Link to="/privacy" target="_blank" data-testid="register-privacy-link" className="text-brand-saffron hover:underline">Privacy Policy</Link>.
                        </span>
                    </label>

                    <Button data-testid="register-submit-btn" type="submit" disabled={loading || !accepted}
                        className="w-full h-12 bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                        {loading ? "Creating..." : "Create account"}
                    </Button>
                </form>

                <p className="text-white/60 text-sm mt-6 text-center">
                    Already have one? <Link to="/login" className="text-brand-saffron hover:underline">Log in</Link>
                </p>
            </div>
        </main>
    );
}
